import type { SetURLSearchParams } from 'react-router-dom';
import type { FabScholarshipParams, FabSchoolParams } from '@/lib/types';

/**
 * The SEED FAB page's filters, held in the URL (house rule: list state lives in
 * search params). Every default is an ABSENT key, and nothing is written on
 * arrival.
 *
 * | Field              | URL key      | Default (absent)          |
 * |--------------------|--------------|---------------------------|
 * | cycleId            | `cycle`      | null = SEED FAB's current |
 * | stage              | `stage`      | `applications`            |
 * | regionIds          | `regions`    | []                        |
 * | countryIds         | `countries`  | []                        |
 * | sources            | `sources`    | []                        |
 * | products           | `products`   | []                        |
 * | since / until      | `since`/`until` | null                   |
 * | intakeSplit        | `intake`     | false (`1` = true)        |
 * | seasonIds          | `seasons`    | []                        |
 * | round              | `round`      | null (1–4)                |
 * | evaluationStatus   | `evaluation` | `all`                     |
 * | submissionStatus   | `submission` | `submitted`               |
 * | search             | `q`          | ''                        |
 *
 * `since`/`until` are ONE date range on the page that means "recorded" for
 * schools and "completed" for scholarships; the two `to*Params` helpers own
 * that mapping. FAB cycles are SEED FAB's own (Jun–May), never EMERGE cycles.
 */
export type FabStage = 'applications' | 'admits' | 'enrolled';

export interface FabFilterState {
  cycleId: number | null;
  stage: FabStage;
  regionIds: number[];
  countryIds: number[];
  sources: string[];
  products: string[];
  since: string | null;
  until: string | null;
  intakeSplit: boolean;
  seasonIds: number[];
  round: number | null;
  evaluationStatus: string;
  submissionStatus: string;
  search: string;
}

export const FAB_STAGES: readonly FabStage[] = ['applications', 'admits', 'enrolled'];
export const DEFAULT_FAB_STAGE: FabStage = 'applications';
export const DEFAULT_EVALUATION_STATUS = 'all';
export const DEFAULT_SUBMISSION_STATUS = 'submitted';

export const FAB_FILTER_KEYS = [
  'cycle',
  'stage',
  'regions',
  'countries',
  'sources',
  'products',
  'since',
  'until',
  'intake',
  'seasons',
  'round',
  'evaluation',
  'submission',
  'q',
] as const;

export const DEFAULT_FAB_FILTER_STATE: FabFilterState = {
  cycleId: null,
  stage: DEFAULT_FAB_STAGE,
  regionIds: [],
  countryIds: [],
  sources: [],
  products: [],
  since: null,
  until: null,
  intakeSplit: false,
  seasonIds: [],
  round: null,
  evaluationStatus: DEFAULT_EVALUATION_STATUS,
  submissionStatus: DEFAULT_SUBMISSION_STATUS,
  search: '',
};

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

function parsePositiveInt(raw: string | null): number | null {
  if (raw === null || !/^\d+$/.test(raw)) return null;
  const n = Number.parseInt(raw, 10);
  return Number.isSafeInteger(n) && n > 0 ? n : null;
}

function parseIdList(raw: string | null): number[] {
  if (!raw) return [];
  const out: number[] = [];
  for (const part of raw.split(',')) {
    const n = parsePositiveInt(part.trim());
    if (n !== null && !out.includes(n)) out.push(n);
  }
  return out;
}

function parseStringList(raw: string | null): string[] {
  if (!raw) return [];
  const out: string[] = [];
  for (const part of raw.split(',')) {
    const v = part.trim();
    if (v !== '' && !out.includes(v)) out.push(v);
  }
  return out;
}

function parseDate(raw: string | null): string | null {
  return raw !== null && DATE_RE.test(raw) ? raw : null;
}

function parseStage(raw: string | null): FabStage {
  return FAB_STAGES.find((s) => s === raw) ?? DEFAULT_FAB_STAGE;
}

function parseRound(raw: string | null): number | null {
  const n = parsePositiveInt(raw);
  return n !== null && n >= 1 && n <= 4 ? n : null;
}

function parseText(raw: string | null, fallback: string): string {
  const v = raw?.trim();
  return v ? v : fallback;
}

export function readFilterState(searchParams: URLSearchParams): FabFilterState {
  return {
    cycleId: parsePositiveInt(searchParams.get('cycle')),
    stage: parseStage(searchParams.get('stage')),
    regionIds: parseIdList(searchParams.get('regions')),
    countryIds: parseIdList(searchParams.get('countries')),
    sources: parseStringList(searchParams.get('sources')),
    products: parseStringList(searchParams.get('products')),
    since: parseDate(searchParams.get('since')),
    until: parseDate(searchParams.get('until')),
    intakeSplit: searchParams.get('intake') === '1',
    seasonIds: parseIdList(searchParams.get('seasons')),
    round: parseRound(searchParams.get('round')),
    evaluationStatus: parseText(searchParams.get('evaluation'), DEFAULT_EVALUATION_STATUS),
    submissionStatus: parseText(searchParams.get('submission'), DEFAULT_SUBMISSION_STATUS),
    search: searchParams.get('q') ?? '',
  };
}

/** Writes `state` onto a copy of `params`: defaults delete their key. */
export function applyFilterState(params: URLSearchParams, state: FabFilterState): URLSearchParams {
  const next = new URLSearchParams(params);
  const put = (key: (typeof FAB_FILTER_KEYS)[number], value: string | null) => {
    if (value === null || value === '') next.delete(key);
    else next.set(key, value);
  };
  put('cycle', state.cycleId === null ? null : String(state.cycleId));
  put('stage', state.stage === DEFAULT_FAB_STAGE ? null : state.stage);
  put('regions', state.regionIds.join(','));
  put('countries', state.countryIds.join(','));
  put('sources', state.sources.join(','));
  put('products', state.products.join(','));
  put('since', state.since);
  put('until', state.until);
  put('intake', state.intakeSplit ? '1' : null);
  put('seasons', state.seasonIds.join(','));
  put('round', state.round === null ? null : String(state.round));
  put(
    'evaluation',
    state.evaluationStatus === DEFAULT_EVALUATION_STATUS ? null : state.evaluationStatus,
  );
  put(
    'submission',
    state.submissionStatus === DEFAULT_SUBMISSION_STATUS ? null : state.submissionStatus,
  );
  put('q', state.search.trim());
  return next;
}

/** Replaces (never pushes) the URL's filter keys; other keys (`tab`) are kept. */
export function writeFilterState(state: FabFilterState, setSearchParams: SetURLSearchParams): void {
  setSearchParams((prev) => applyFilterState(prev, state), { replace: true });
}

/** Removes every filter key except `cycle`, which the School links tab shares. */
export function clearFilterKeys(params: URLSearchParams): URLSearchParams {
  const next = new URLSearchParams(params);
  for (const key of FAB_FILTER_KEYS) {
    if (key !== 'cycle') next.delete(key);
  }
  return next;
}

/* --- tabs and the Schools stage toggle (client-side views) ---------------- */

/** The page's tabs; URL key `tab`, absent = `overview`. */
export type FabTab = 'overview' | 'schools' | 'scholarships' | 'links';
export const FAB_TABS: readonly FabTab[] = ['overview', 'schools', 'scholarships', 'links'];
export const DEFAULT_FAB_TAB: FabTab = 'overview';

export function readFabTab(searchParams: URLSearchParams): FabTab {
  const raw = searchParams.get('tab');
  return FAB_TABS.find((t) => t === raw) ?? DEFAULT_FAB_TAB;
}

/**
 * A copy of `params` on `tab`. A tab switch clears `q` (the per-tab search)
 * and `stage` when leaving Schools; every other filter is shared and kept.
 * `extra` is written after (e.g. `{ q: name }` or `{ stage: 'admits' }` for a
 * funnel/top-list click-through); an empty or default value deletes its key.
 */
export function withTab(
  params: URLSearchParams,
  tab: FabTab,
  extra: { q?: string; stage?: FabStage } = {},
): URLSearchParams {
  const next = new URLSearchParams(params);
  if (tab === DEFAULT_FAB_TAB) next.delete('tab');
  else next.set('tab', tab);
  next.delete('q');
  if (tab !== 'schools') next.delete('stage');
  const q = extra.q?.trim();
  if (q) next.set('q', q);
  return extra.stage === undefined ? next : withStage(next, extra.stage);
}

/** A copy of `params` with the Schools stage toggle set (default deletes the key). */
export function withStage(params: URLSearchParams, stage: FabStage): URLSearchParams {
  const next = new URLSearchParams(params);
  if (stage === DEFAULT_FAB_STAGE) next.delete('stage');
  else next.set('stage', stage);
  return next;
}

export function filterStatesEqual(a: FabFilterState, b: FabFilterState): boolean {
  return applyFilterState(new URLSearchParams(), a).toString() ===
    applyFilterState(new URLSearchParams(), b).toString();
}

function nonEmpty<T>(list: T[]): T[] | undefined {
  return list.length > 0 ? list : undefined;
}

/**
 * Schools query. The page's date range is the RECORDED range here. Absent keys
 * are left out (not sent as undefined values) so the API's `listQuery` and the
 * cache key stay stable.
 */
export function toSchoolParams(state: FabFilterState): FabSchoolParams {
  const params: FabSchoolParams = { stage: state.stage };
  if (state.cycleId !== null) params.cycleId = state.cycleId;
  const regionIds = nonEmpty(state.regionIds);
  if (regionIds) params.regionIds = regionIds;
  const countryIds = nonEmpty(state.countryIds);
  if (countryIds) params.countryIds = countryIds;
  const sources = nonEmpty(state.sources);
  if (sources) params.source = sources;
  const products = nonEmpty(state.products);
  if (products) params.product = products;
  if (state.since !== null) params.recordedSince = state.since;
  if (state.until !== null) params.recordedUntil = state.until;
  if (state.intakeSplit) params.intakeSplit = true;
  const seasonIds = nonEmpty(state.seasonIds);
  if (seasonIds) params.seasonIds = seasonIds;
  const search = state.search.trim();
  if (search !== '') params.search = search;
  return params;
}

/** Scholarships query. The page's date range is the COMPLETED range here. */
export function toScholarshipParams(state: FabFilterState): FabScholarshipParams {
  const params: FabScholarshipParams = {
    evaluationStatus: state.evaluationStatus,
    submissionStatus: state.submissionStatus,
  };
  if (state.cycleId !== null) params.cycleId = state.cycleId;
  const regionIds = nonEmpty(state.regionIds);
  if (regionIds) params.regionIds = regionIds;
  const countryIds = nonEmpty(state.countryIds);
  if (countryIds) params.countryIds = countryIds;
  if (state.round !== null) params.round = state.round;
  if (state.since !== null) params.completedSince = state.since;
  if (state.until !== null) params.completedUntil = state.until;
  if (state.intakeSplit) params.intakeSplit = true;
  const seasonIds = nonEmpty(state.seasonIds);
  if (seasonIds) params.seasonIds = seasonIds;
  const search = state.search.trim();
  if (search !== '') params.search = search;
  return params;
}
